import { formatTime, isSameDay } from '@crm/shared';
import { cn } from '@crm/ui';
import { useEffect, useState } from 'react';
import type { Appointment } from '../api';
import { STATUS_BLOCK } from './calendar-colors';

/** Faixa do dia que a grade mostra, no formato do Google Agenda. */
const START_HOUR = 7;
const END_HOUR = 22;
const HOUR_HEIGHT = 48;
const weekDayLabel = new Intl.DateTimeFormat('pt-BR', { weekday: 'short' });

function minutesFromStart(date: Date): number {
  return (date.getHours() - START_HOUR) * 60 + date.getMinutes();
}

/**
 * Distribui em faixas os agendamentos que se sobrepoem, para dividirem a
 * largura da coluna em vez de um cobrir o outro.
 *
 * A divisao vale so dentro do grupo que de fato se cruza: dois horarios
 * colados nao podem espremer o resto do dia, que continua ocupando a coluna
 * inteira.
 */
export function toLanes(items: Appointment[]) {
  const sorted = [...items].sort(
    (a, b) => new Date(a.startAt).getTime() - new Date(b.startAt).getTime(),
  );

  const placed: { item: Appointment; lane: number; columns: number }[] = [];
  /** Grupo corrente de horarios que se cruzam, e o fim de cada faixa dele. */
  let group: { item: Appointment; lane: number; columns: number }[] = [];
  let laneEnds: number[] = [];
  let groupEnd = 0;

  const closeGroup = () => {
    if (group.length === 0) return;

    const columns = Math.max(1, laneEnds.length);
    for (const entry of group) entry.columns = columns;

    placed.push(...group);
    group = [];
    laneEnds = [];
    groupEnd = 0;
  };

  for (const item of sorted) {
    const start = new Date(item.startAt).getTime();
    const end = new Date(item.endAt).getTime();

    // Comecou depois do fim de todo mundo do grupo: e outro grupo.
    if (start >= groupEnd) closeGroup();

    let lane = laneEnds.findIndex((laneEnd) => laneEnd <= start);

    if (lane === -1) {
      laneEnds.push(end);
      lane = laneEnds.length - 1;
    } else {
      laneEnds[lane] = end;
    }

    groupEnd = Math.max(groupEnd, end);
    group.push({ item, lane, columns: 1 });
  }

  closeGroup();

  return placed;
}

function DayColumn({
  day,
  items,
  onSelect,
  onCreateAt,
}: {
  day: Date;
  items: Appointment[];
  onSelect: (id: string) => void;
  /** Clique num espaco livre da grade, arredondado para 15 min. */
  onCreateAt?: (start: Date) => void;
}) {
  const placed = toLanes(items);

  return (
    <div
      className="relative border-l border-border"
      style={{ height: (END_HOUR - START_HOUR) * HOUR_HEIGHT }}
    >
      {Array.from({ length: END_HOUR - START_HOUR }, (_, index) => START_HOUR + index).map(
        (hour) =>
          onCreateAt ? (
            // Cada hora vira quatro faixas de 15 min: clicar numa delas abre o
            // agendamento ja naquele horario.
            <div key={hour} className="border-t border-border/60" style={{ height: HOUR_HEIGHT }}>
              {[0, 15, 30, 45].map((minute) => {
                const label = `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`;

                return (
                  <button
                    key={minute}
                    type="button"
                    // A dica diz a que horas o clique marca - a faixa de 15
                    // min e fina demais para se ler pela regua.
                    title={`Novo agendamento as ${label}`}
                    aria-label={`Novo agendamento as ${label}`}
                    onClick={() =>
                      onCreateAt(
                        new Date(day.getFullYear(), day.getMonth(), day.getDate(), hour, minute),
                      )
                    }
                    className="group flex w-full items-center px-2 transition-colors hover:bg-primary/10"
                    style={{ height: HOUR_HEIGHT / 4 }}
                  >
                    <span className="hidden text-xs font-medium text-primary group-hover:inline">
                      {label}
                    </span>
                  </button>
                );
              })}
            </div>
          ) : (
            <div
              key={hour}
              className="border-t border-border/60"
              style={{ height: HOUR_HEIGHT }}
              aria-hidden="true"
            />
          ),
      )}

      {placed.map(({ item, lane, columns }) => {
        const start = new Date(item.startAt);
        const top = Math.max(0, (minutesFromStart(start) / 60) * HOUR_HEIGHT);
        const height = Math.max(20, (item.durationMinutes / 60) * HOUR_HEIGHT - 2);
        // Bloco curto nao tem altura para duas linhas: a segunda apareceria
        // cortada ao meio. Nesse caso o servico entra na propria linha do
        // nome - some da agenda era pior que ficar truncado.
        const twoLines = height >= 46;

        return (
          <button
            type="button"
            key={item.id}
            onClick={() => onSelect(item.id)}
            title={`${formatTime(item.startAt)} ${item.customerName} - ${item.serviceSummary}`}
            className={cn(
              'absolute overflow-hidden rounded px-2 py-0.5 text-left text-sm leading-snug shadow-sm transition-opacity hover:opacity-90',
              STATUS_BLOCK[item.status],
            )}
            style={{
              top,
              height,
              left: `calc(${(lane / columns) * 100}% + 2px)`,
              width: `calc(${100 / columns}% - 4px)`,
            }}
          >
            <span className="block truncate font-medium">
              {formatTime(item.startAt)} {item.customerName}
              {!twoLines && item.serviceSummary ? ` (${item.serviceSummary})` : ''}
            </span>
            {twoLines ? (
              <span className="block truncate opacity-90">{item.serviceSummary}</span>
            ) : null}
          </button>
        );
      })}

      <NowLine day={day} />
    </div>
  );
}

/** Linha vermelha do horario atual, so no dia de hoje. */
function NowLine({ day }: { day: Date }) {
  const [now, setNow] = useState(() => new Date());

  useEffect(() => {
    const timer = setInterval(() => setNow(new Date()), 60_000);
    return () => clearInterval(timer);
  }, []);

  if (!isSameDay(day, now)) return null;

  const minutes = minutesFromStart(now);
  if (minutes < 0 || minutes > (END_HOUR - START_HOUR) * 60) return null;

  return (
    <div
      className="pointer-events-none absolute inset-x-0 z-10 flex items-center"
      style={{ top: (minutes / 60) * HOUR_HEIGHT }}
    >
      <span className="-ml-1 h-2 w-2 rounded-full bg-red-600" />
      <span className="h-px flex-1 bg-red-600" />
    </div>
  );
}

interface TimeGridProps {
  days: Date[];
  appointments: Appointment[];
  onSelect: (id: string) => void;
  /** Sem isto a grade e so de leitura: nenhuma faixa vazia responde ao clique. */
  onCreateAt?: (start: Date) => void;
}

/** Grade de horarios das visoes de dia e semana. */
export function TimeGrid({ days, appointments, onSelect, onCreateAt }: TimeGridProps) {
  const today = new Date();

  return (
    // A rolagem lateral e da semana, que nao cabe na tela. A vertical vem de
    // brinde: com `overflow-x: auto` o navegador liga o eixo Y junto e um
    // arredondamento de 1px basta para nascer uma segunda barra dentro da
    // pagina. Travar o Y resolve - a grade tem a altura exata do seu conteudo.
    <div className="overflow-x-auto overflow-y-hidden">
      {/* Coluna de dia com largura util: mais estreita que isso, o cartao vira
          "0..." e o horario deixa de ser legivel. Rola de lado, e o certo. */}
      <div
        // A grade ocupa o quadro inteiro: limitada e centralizada, sobrava
        // branco dos dois lados e o dia parecia vazio.
        className={cn(days.length === 1 && 'min-w-0')}
        style={days.length > 1 ? { minWidth: `${3.5 + days.length * 6.5}rem` } : undefined}
      >
        {/* Cabecalho dos dias */}
        <div
          className="grid border-b border-border"
          style={{ gridTemplateColumns: `3.5rem repeat(${days.length}, minmax(0, 1fr))` }}
        >
          <div />
          {days.map((day) => {
            const isToday = isSameDay(day, today);

            return (
              <div key={day.toISOString()} className="px-1 py-2 text-center">
                <p className="text-sm uppercase text-muted-foreground">
                  {weekDayLabel.format(day)}
                </p>
                <p
                  className={cn(
                    'mx-auto mt-1 flex h-9 w-9 items-center justify-center rounded-full text-xl',
                    isToday ? 'bg-primary font-medium text-primary-foreground' : 'text-foreground',
                  )}
                >
                  {day.getDate()}
                </p>
              </div>
            );
          })}
        </div>

        {/* Régua de horas + colunas */}
        <div
          className="grid"
          style={{ gridTemplateColumns: `3.5rem repeat(${days.length}, minmax(0, 1fr))` }}
        >
          <div>
            {Array.from({ length: END_HOUR - START_HOUR }, (_, index) => START_HOUR + index).map(
              (hour) => (
                <div
                  key={hour}
                  className="relative pr-2 text-right"
                  style={{ height: HOUR_HEIGHT }}
                >
                  {/* O rotulo comeca na linha da hora, na mesma altura do topo
                      do bloco daquele horario. */}
                  <span className="absolute right-2 top-0 text-sm font-medium leading-tight text-foreground/75">
                    {hour === START_HOUR ? '' : `${String(hour).padStart(2, '0')}:00`}
                  </span>
                </div>
              ),
            )}
          </div>

          {days.map((day) => (
            <DayColumn
              key={day.toISOString()}
              day={day}
              items={appointments.filter((item) => isSameDay(new Date(item.startAt), day))}
              onSelect={onSelect}
              onCreateAt={onCreateAt}
            />
          ))}
        </div>
      </div>
    </div>
  );
}
